import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Database, Layers, GitBranch, Shield, Lock, Users, School, ChefHat, Building2, Landmark, CheckCircle, Eye, Award, Zap, Code, Server, Link2, Cpu } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PagePlatformEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Platform | Architecture"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="platform" lang="en" />

      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Database size={16} />
            <span>Architecture & Security</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span className="gradient-text">Architecture</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--color-lime)' }}>ONE</strong> centralized system • <strong style={{ color: 'var(--color-lime)' }}>FIVE</strong> specialized portals<br />
            Multi-tenant architecture • Centralized data • Enterprise security
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Centralized system</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">5</div>
              <div className="stat-label">Specialized portals</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Secure</div>
            </div>
          </div>
        </div>
      </section>

      <section id="architecture" className="section architecture-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">System Architecture</h2>
            <p className="section-subtitle">
              A centralized platform with 5 interconnected portals
            </p>
          </div>

          <div className="architecture-diagram">
            <div className="hub-spoke-container" data-testid="hub-spoke-diagram">
              <div className="architecture-center">
                <div className="center-hub">
                  <img src={require('../assets/logo-fox-neon.png')} alt="EcoLunch" className="center-hub-fox" />
                  <span>EcoLunch</span>
                  <p>Centralized Backend</p>
                </div>
              </div>

              <div className="hub-spoke-orbit">
                <svg className="hub-spoke-lines" viewBox="0 0 620 620">
                  <line className="hub-spoke-line" x1="310" y1="310" x2="310" y2="120" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="491" y2="257" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="424" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="196" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="129" y2="257" />
                </svg>
              </div>

              <div className="hub-spoke-nodes">
                <div className="actor-node parent-node spoke-pos-0" onClick={() => navigate('/en/parents')} data-testid="spoke-parents">
                  <Users size={24} />
                  <h4>Parents</h4>
                  <p>Family Interface</p>
                </div>
                <div className="actor-node caterer-node spoke-pos-1" onClick={() => navigate('/en/caterers')} data-testid="spoke-caterers">
                  <ChefHat size={24} />
                  <h4>Caterers</h4>
                  <p>Back-Office</p>
                </div>
                <div className="actor-node school-node spoke-pos-2" onClick={() => navigate('/en/schools')} data-testid="spoke-schools">
                  <School size={24} />
                  <h4>Schools</h4>
                  <p>Cockpit</p>
                </div>
                <div className="actor-node direction-node spoke-pos-3" onClick={() => navigate('/en/directions')} data-testid="spoke-directions">
                  <Building2 size={24} />
                  <h4>Boards</h4>
                  <p>Governance</p>
                </div>
                <div className="actor-node css-node spoke-pos-4" onClick={() => navigate('/en/css')} data-testid="spoke-css">
                  <Landmark size={24} />
                  <h4>CSS</h4>
                  <p>Policy</p>
                </div>
              </div>
            </div>
          </div>

          <div className="architecture-features">
            <div className="arch-feature">
              <div className="arch-feature-icon"><Layers size={24} /></div>
              <h4>One system</h4>
              <p>No multiple sites, no fragmentation</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><GitBranch size={24} /></div>
              <h4>Centralized data</h4>
              <p>Information flows orchestrated in real time</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><Shield size={24} /></div>
              <h4>Secure isolation</h4>
              <p>Each actor in their protected bubble</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section flow-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Operational Flow</h2>
            <p className="section-subtitle">
              How the 5 actors interact in the system
            </p>
          </div>

          <div className="flow-steps">
            <div className="flow-step">
              <div className="flow-number">1</div>
              <div className="flow-content">
                <Users size={28} />
                <h4>Parent orders</h4>
                <p>A parent orders for all their children in <strong>15 seconds</strong> via the app</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">2</div>
              <div className="flow-content">
                <ChefHat size={28} />
                <h4>Caterer receives automatically</h4>
                <p>The order is <strong>automatically integrated</strong> into the caterer's production sheet</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">3</div>
              <div className="flow-content">
                <School size={28} />
                <h4>School sees in real time</h4>
                <p>The school sees the adjusted volume on their dashboard <strong>instantly</strong></p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">4</div>
              <div className="flow-content">
                <Building2 size={28} />
                <h4>Management supervises</h4>
                <p>Management sees the participation increase in their consolidated view</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">5</div>
              <div className="flow-content">
                <Landmark size={28} />
                <h4>CSS drives food policy</h4>
                <p>The School Service Centre analyzes consolidated data and adjusts <strong>food policies</strong> across the territory</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="security" className="section security-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Security & Privacy</h2>
            <p className="section-subtitle">
              Enterprise-level protection for all your data
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Multi-Tenant Architecture</h3>
              <p>Complete data isolation between organizations. Each school board in its own secure space.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Lock size={32} /></div>
              <h3>End-to-end encryption</h3>
              <p>All sensitive data is encrypted in transit (TLS 1.3) and at rest (AES-256).</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Hierarchical permissions</h3>
              <p>RBAC (Role-Based Access Control) system. Each actor sees only what concerns them.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>GDPR/PIPEDA compliance</h3>
              <p>Full compliance with Quebec and Canadian data protection regulations.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Server size={32} /></div>
              <h3>Quebec Hosting</h3>
              <p>Servers located in Quebec. Your data never leaves Quebec territory.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Certifications</h3>
              <p>SOC 2 Type II compliance. Regular security audits by independent third parties.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sovereign Hosting — Dedicated Tile */}
      <section className="section" data-testid="hosting-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Sovereign Infrastructure</h2>
            <p className="section-subtitle">
              Your data stays in Quebec, hosted by a global leader
            </p>
          </div>

          <div className="hosting-tile" data-testid="hosting-tile">
            <div className="hosting-tile-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Server size={35} />
              </div>
              <div>
                <h3>Quebec Sovereign Hosting</h3>
                <span className="hosting-tile-badge">OVH Cloud Infrastructure</span>
              </div>
            </div>
            <div className="hosting-tile-body">
              <p>
                Quebec infrastructure <strong>(Montreal)</strong> hosted by <strong>OVH Cloud</strong>, 
                global leader in data sovereignty. No data is transferred abroad.
              </p>
              <div className="hosting-tile-divider"></div>
              <p className="hosting-tile-arch">
                Enterprise-grade multi-tenant architecture: each School Board, each School, 
                each Caterer operates in its own secure and fully isolated space.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section benefits-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Modern Tech Stack</h2>
            <p className="section-subtitle">
              Cutting-edge technologies for performance and reliability
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Code size={32} /></div>
              <h3>Scalable backend</h3>
              <p>Microservices architecture with RESTful API. Capable of handling thousands of simultaneous requests.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Database size={32} /></div>
              <h3>Optimized database</h3>
              <p>MongoDB for flexibility and performance. Daily automatic replication and backup.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Real time</h3>
              <p>WebSockets for instant updates. Dashboards refresh automatically.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>99.9% availability</h3>
              <p>Guaranteed SLA. Redundant infrastructure with automatic failover in case of outage.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Integrations & Technologies</h2>
            <p className="section-subtitle">
              Connect EcoLunch to your existing ecosystem
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Link2 size={32} /></div>
              <h3>API with Current Systems</h3>
              <p>Modular API - we will adapt our platform to your current management to centralize all operations. Everything done on EcoLunch will be reflected in each of your systems. Available integrations: Management system, Accounting system, Acomba & Acomba GO.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Cpu size={32} /></div>
              <h3>Unique Technology</h3>
              <p>AI developed exclusively for food catering management. Proprietary algorithms trained on years of sector data. Programmed specifically to optimize every aspect of your operations. No generic AI - 100% adapted to YOUR needs.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Code size={32} /></div>
              <h3>Scalable Architecture</h3>
              <p>Platform designed to adapt to your future needs. Adding new modules without service interruption. Continuous updates without impact on your operations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto — Local Foundation */}
      <section className="section" data-testid="manifeste-platform">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="manifeste-tile" data-testid="manifeste-fondation-platform">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}>
                <Award size={30} />
              </div>
              <h3>A serious, local and credible foundation</h3>
            </div>
            <p>
              The technological and partnership approach behind EcoLunch relies on recognized players:
            </p>
            <p>
              <strong>Sezzle Canada</strong> — flexible payments<br />
              <strong>Paysafe Montreal</strong> — secure processing<br />
              <strong>OVH Cloud Quebec</strong> — sovereign hosting
            </p>
          </div>
        </div>
      </section>
      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PagePlatformEN;
